import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import sortBy from 'lodash/sortBy';
import pick from 'lodash/pick';
import uniqWith from 'lodash/uniqWith';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Actions
import { fetchCaptureData } from 'Shared/actions/workspaces';

// Utils
import { trackViewedSurfaceFeed } from 'Shared/tracking/tracking';

// Components
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import SurfaceAITable from 'Shared/components/SurfaceAI/SurfaceAITable';
import SurfaceAIFilters from 'Shared/components/SurfaceAI/SurfaceAIFilters';
import AddSurfaceItemToWorkspaceModal from 'Shared/components/AddSurfaceItemToWorkspaceModal/AddSurfaceItemToWorkspaceModal';
import SurfaceAIEmptyState from './SurfaceAIEmptyState';

// Styles
import './SurfaceAI.scss';

const SurfaceAI = () => {
  const dispatch = useDispatch();
  const [isLoading, toggleIsLoading] = useToggle(true);
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const [surfaceData, setSurfaceData] = useState(null);
  const [surfaceIntegrations, setSurfaceIntegrations] = useState(null);
  const [surfaceFilters, setSurfaceFilters] = useState({ name: '', typeName: [], insight: [], users: [], status: [] });
  const [selectedSurfaceItem, setSelectedSurfaceItem] = useState(null);

  const uniqueTypes =
    surfaceData &&
    uniqWith(surfaceData, (A, B) => A.type === B.type && A.subtype === B.subtype).map(item =>
      pick(item, ['type', 'subtype'])
    );

  const hasSurfaceIntegrations = surfaceIntegrations && surfaceIntegrations.length > 0;
  const hasSurfaceIntegrationThatIsLoading =
    surfaceIntegrations && surfaceIntegrations.find(({ last_computed_at }) => !last_computed_at);
  const hasSurfaceData = surfaceData && uniqueTypes.length > 0;

  // TODO: this will be an issue if the all hands is ever deleted
  // or removed from an org
  const workspace = useSelector(
    state =>
      state.workspaces.find(({ isTeamSync }) => isTeamSync) ||
      sortBy(
        state.workspaces.filter(({ isGroup }) => isGroup),
        w => -w.users.length
      )[0]
  );

  const workspaceId = workspace?.id;

  useEffect(() => {
    trackViewedSurfaceFeed();
  }, []);

  useEffect(() => {
    if (!workspace) return;
    dispatch(fetchCaptureData({ workspaceId, params: { version: '3' } }))
      .then(response => {
        setSurfaceData(response.data);
        setSurfaceIntegrations(response.surface_integrations);
      })
      .finally(() => {
        toggleIsLoading();
      });
  }, [workspace]);

  const formatData = () => {
    if (!surfaceData) return null;
    return surfaceData;
  };

  const handleAddToWorkspaceClick = surfaceItem => {
    setSelectedSurfaceItem(surfaceItem);
    toggleIsModalOpen();
  };

  const handleUpdateSurfaceFilters = filters => {
    setSurfaceFilters({ ...surfaceFilters, ...filters });
    const filterCategory = Object.keys(filters)[0];
    (window as any).analytics.track('Used Surface Feed Filters', {
      filterCategory,
      filters: filters[filterCategory].length,
    });
  };

  const SurfaceItemActions = ({ selectedItem }) => {
    return (
      <WpButton variant="tertiary" onClick={() => handleAddToWorkspaceClick(selectedItem)}>
        <WpIcon name={'plus-circle'} />
      </WpButton>
    );
  };

  const sortedData = formatData();
  const baseClassName = 'SurfaceAI';

  if (!isLoading && !hasSurfaceData) {
    return (
      <SurfaceAIEmptyState
        hasSurfaceIntegrations={hasSurfaceIntegrations}
        hasSurfaceIntegrationThatIsLoading={hasSurfaceIntegrationThatIsLoading}
      />
    );
  }

  return (
    <>
      <div className={baseClassName}>
        <div className={`${baseClassName}__filters`}>
          <SurfaceAIFilters
            surfaceData={sortedData}
            filters={surfaceFilters}
            updateFilters={handleUpdateSurfaceFilters}
          />
        </div>
        <div className={`${baseClassName}__table`}>
          <SurfaceAITable
            surfaceData={sortedData}
            filters={surfaceFilters}
            loading={isLoading}
            surfaceItemActions={SurfaceItemActions}
          />
        </div>
      </div>
      <AddSurfaceItemToWorkspaceModal
        isOpen={isModalOpen}
        toggleModal={toggleIsModalOpen}
        surfaceItem={selectedSurfaceItem}
      />
    </>
  );
};

export default SurfaceAI;
